/**
 * Dynamic block authoring mutations (DYN-1.7 + DYN-1.8).
 *
 * These mutations modify a BlockDefinition's `dynamicMetadata` subtree
 * without touching the underlying block entities. Every mutation returns
 * the updated DrawingDocument and a human-readable summary.
 *
 * Public surface:
 *   - addDynamicParameterMutation
 *   - updateDynamicParameterMutation
 *   - removeDynamicParameterMutation   (cascade removes actions referencing the parameter)
 *   - addDynamicActionMutation
 *   - updateDynamicActionMutation
 *   - removeDynamicActionMutation
 *   - addVisibilityStateMutation
 *   - renameVisibilityStateMutation
 *   - deleteVisibilityStateMutation
 *   - reorderVisibilityStatesMutation
 *   - setDefaultVisibilityStateMutation
 *   - setCurrentVisibilityStateMutation       (Phase K.4 — BVSTATE current pick)
 *   - toggleVisibilityStateEntityMutation
 *   - bvhideMutation                          (Phase K.4 — BVHIDE current state)
 *   - bvshowMutation                          (Phase K.4 — BVSHOW current state)
 *   - bvshowAllMutation                       (Phase K.4 — BVSHOW _All)
 *
 * All mutations enforce:
 *   - the target block must exist in `document.blocks`;
 *   - `dynamicMetadata` is lazily created with version=1 defaults when a
 *     parameter is added for the first time;
 *   - `validateDynamicMetadata` is run after every write and any semantic
 *     error aborts the mutation (throws) — the shell reducer converts
 *     these into a toast.
 *
 * BEDIT gating is NOT enforced here — the reducer is expected to check
 * that the active space is `block-edit` and that the block name matches
 * the active BEDIT target before invoking these helpers. Keeping the
 * guard out of the mutation layer mirrors attributeMutations / attribute
 * helpers (BEDIT gating lives in attributeReducerHelpers).
 */

import type {
  BlockDefinition,
  DrawingDocument,
  DynamicAction,
  DynamicBlockMetadata,
  DynamicParameter,
  VisibilityParameter,
  VisibilityState,
} from "@dxf-ai-editor/drawing-model";
import {
  deriveEvaluationEdges,
  dynamicBlockMetadataSchema,
  validateDynamicMetadata,
} from "@dxf-ai-editor/drawing-model";

export interface MutationResult {
  updatedDocument: DrawingDocument;
  summary: string;
}

// ═══════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════

function requireBlock(
  document: DrawingDocument,
  blockName: string,
): BlockDefinition {
  const blockDef = document.blocks[blockName];
  if (!blockDef) {
    throw new Error(`Block '${blockName}' not found`);
  }
  return blockDef;
}

function ensureMetadata(blockDef: BlockDefinition): DynamicBlockMetadata {
  if (blockDef.dynamicMetadata) return blockDef.dynamicMetadata;
  return dynamicBlockMetadataSchema.parse({});
}

function replaceBlock(
  document: DrawingDocument,
  blockName: string,
  update: BlockDefinition,
): DrawingDocument {
  return {
    ...document,
    blocks: { ...document.blocks, [blockName]: update },
  };
}

function validateAndCommit(
  document: DrawingDocument,
  blockDef: BlockDefinition,
  nextMeta: DynamicBlockMetadata,
  summary: string,
): MutationResult {
  const parsed = dynamicBlockMetadataSchema.parse(nextMeta);
  const errors = validateDynamicMetadata(parsed);
  if (errors.length > 0) {
    throw new Error(`Invalid dynamic metadata: ${errors.join("; ")}`);
  }
  const nextBlockDef: BlockDefinition = {
    ...blockDef,
    dynamicMetadata: parsed,
  };
  return {
    updatedDocument: replaceBlock(document, blockDef.name, nextBlockDef),
    summary,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// ADD / UPDATE / REMOVE parameter
// ═══════════════════════════════════════════════════════════════════════

export interface AddDynamicParameterInput {
  document: DrawingDocument;
  blockName: string;
  parameter: DynamicParameter;
}

export function addDynamicParameterMutation(
  input: AddDynamicParameterInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  // Name / id must be unique.
  if (meta.parameters.some((p) => p.id === input.parameter.id)) {
    throw new Error(`Parameter id '${input.parameter.id}' already exists`);
  }
  if (meta.parameters.some((p) => p.name === input.parameter.name)) {
    throw new Error(
      `Parameter name '${input.parameter.name}' already exists`,
    );
  }
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    parameters: [...meta.parameters, input.parameter],
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Added ${input.parameter.kind} parameter '${input.parameter.name}' to block '${input.blockName}'`,
  );
}

export interface UpdateDynamicParameterInput {
  document: DrawingDocument;
  blockName: string;
  parameterId: string;
  updates: Partial<DynamicParameter>;
}

export function updateDynamicParameterMutation(
  input: UpdateDynamicParameterInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  const existing = meta.parameters.find((p) => p.id === input.parameterId);
  if (!existing) {
    throw new Error(`Parameter '${input.parameterId}' not found`);
  }
  // Merge but keep the same discriminator (kind) — changing kind is
  // considered a separate operation (remove + add).
  const merged = { ...existing, ...input.updates, kind: existing.kind };
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    parameters: meta.parameters.map((p) =>
      p.id === input.parameterId ? (merged as DynamicParameter) : p,
    ),
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Updated parameter '${existing.name}' in block '${input.blockName}'`,
  );
}

export interface RemoveDynamicParameterInput {
  document: DrawingDocument;
  blockName: string;
  parameterId: string;
}

export function removeDynamicParameterMutation(
  input: RemoveDynamicParameterInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  const removed = meta.parameters.find((p) => p.id === input.parameterId);
  if (!removed) {
    throw new Error(`Parameter '${input.parameterId}' not found`);
  }
  // Cascade-remove actions whose parameterId points at the removed param.
  const nextActions = meta.actions.filter(
    (a) => a.parameterId !== input.parameterId,
  );
  const removedActionIds = new Set(
    meta.actions
      .filter((a) => a.parameterId === input.parameterId)
      .map((a) => a.id),
  );
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    parameters: meta.parameters.filter((p) => p.id !== input.parameterId),
    actions: nextActions,
    evaluationGraph: {
      ...meta.evaluationGraph,
      edges: meta.evaluationGraph.edges.filter(
        (e) =>
          e.parameterId !== input.parameterId &&
          !removedActionIds.has(e.actionId),
      ),
      chainGroups: meta.evaluationGraph.chainGroups
        .map((grp) => grp.filter((id) => !removedActionIds.has(id)))
        .filter((grp) => grp.length > 0),
    },
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Removed parameter '${removed.name}' (and ${removedActionIds.size} linked action(s)) from block '${input.blockName}'`,
  );
}

// ═══════════════════════════════════════════════════════════════════════
// ADD / UPDATE / REMOVE action
// ═══════════════════════════════════════════════════════════════════════

export interface AddDynamicActionInput {
  document: DrawingDocument;
  blockName: string;
  action: DynamicAction;
}

export function addDynamicActionMutation(
  input: AddDynamicActionInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  if (meta.actions.some((a) => a.id === input.action.id)) {
    throw new Error(`Action id '${input.action.id}' already exists`);
  }
  // Validate parameter kind compatibility with action kind.
  const linkedParam = meta.parameters.find(
    (p) => p.id === input.action.parameterId,
  );
  if (!linkedParam) {
    throw new Error(
      `Action references unknown parameter '${input.action.parameterId}'`,
    );
  }
  validateActionParameterCompat(input.action, linkedParam);

  const nextActions = [...meta.actions, input.action];
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    actions: nextActions,
    // Re-derive evaluation edges so every action has a declared edge.
    evaluationGraph: {
      ...meta.evaluationGraph,
      edges: deriveEvaluationEdges(nextActions),
    },
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Added ${input.action.kind} action '${input.action.name || input.action.id}' to block '${input.blockName}'`,
  );
}

export interface UpdateDynamicActionInput {
  document: DrawingDocument;
  blockName: string;
  actionId: string;
  updates: Partial<DynamicAction>;
}

export function updateDynamicActionMutation(
  input: UpdateDynamicActionInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  const existing = meta.actions.find((a) => a.id === input.actionId);
  if (!existing) {
    throw new Error(`Action '${input.actionId}' not found`);
  }
  const merged = { ...existing, ...input.updates, kind: existing.kind };
  if (merged.parameterId !== existing.parameterId) {
    const linkedParam = meta.parameters.find(
      (p) => p.id === merged.parameterId,
    );
    if (!linkedParam) {
      throw new Error(
        `Action references unknown parameter '${merged.parameterId}'`,
      );
    }
    validateActionParameterCompat(merged as DynamicAction, linkedParam);
  }
  const nextActions = meta.actions.map((a) =>
    a.id === input.actionId ? (merged as DynamicAction) : a,
  );
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    actions: nextActions,
    evaluationGraph: {
      ...meta.evaluationGraph,
      edges: deriveEvaluationEdges(nextActions),
    },
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Updated action '${existing.name || existing.id}' in block '${input.blockName}'`,
  );
}

export interface RemoveDynamicActionInput {
  document: DrawingDocument;
  blockName: string;
  actionId: string;
}

export function removeDynamicActionMutation(
  input: RemoveDynamicActionInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  const removed = meta.actions.find((a) => a.id === input.actionId);
  if (!removed) {
    throw new Error(`Action '${input.actionId}' not found`);
  }
  const nextActions = meta.actions.filter((a) => a.id !== input.actionId);
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    actions: nextActions,
    evaluationGraph: {
      ...meta.evaluationGraph,
      edges: deriveEvaluationEdges(nextActions),
      chainGroups: meta.evaluationGraph.chainGroups
        .map((grp) => grp.filter((id) => id !== input.actionId))
        .filter((grp) => grp.length > 0),
    },
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Removed action '${removed.name || removed.id}' from block '${input.blockName}'`,
  );
}

function validateActionParameterCompat(
  action: DynamicAction,
  parameter: DynamicParameter,
): void {
  switch (action.kind) {
    case "move":
    case "stretch":
      if (parameter.kind !== "linear") {
        throw new Error(
          `${action.kind} action requires a linear parameter (got ${parameter.kind})`,
        );
      }
      break;
    case "rotate":
      if (
        parameter.kind !== "rotation" &&
        parameter.kind !== "constraint-angular"
      ) {
        throw new Error(
          `rotate action requires a rotation or angular constraint parameter (got ${parameter.kind})`,
        );
      }
      break;
    case "scale":
    case "array":
      // Mirror `LINEAR_LIKE` from `packages/drawing-model/src/dynamicBlock-
      // Metadata.ts:124` exactly. Tier 3 constraint kinds (aligned / radial
      // / diameter) are also accepted by `allowedParameterKindsForAction`
      // and `isLinearLikeKind`, so this validator must not reject them.
      // Codex P1 review on PR #39 flagged this as an authoring-time
      // regression for Tier-3 dynamic blocks.
      if (
        parameter.kind !== "linear" &&
        parameter.kind !== "polar" &&
        parameter.kind !== "xy" &&
        parameter.kind !== "constraint-horizontal" &&
        parameter.kind !== "constraint-vertical" &&
        parameter.kind !== "constraint-aligned" &&
        parameter.kind !== "constraint-radial" &&
        parameter.kind !== "constraint-diameter"
      ) {
        throw new Error(
          `${action.kind} action requires a distance/vector parameter (got ${parameter.kind})`,
        );
      }
      break;
    case "flip":
      if (parameter.kind !== "flip") {
        throw new Error(
          `flip action requires a flip parameter (got ${parameter.kind})`,
        );
      }
      break;
    case "lookup":
      if (parameter.kind !== "lookup") {
        throw new Error(
          `lookup action requires a lookup parameter (got ${parameter.kind})`,
        );
      }
      break;
    case "polar-stretch":
      if (parameter.kind !== "polar") {
        throw new Error(
          `polar-stretch action requires a polar parameter (got ${parameter.kind})`,
        );
      }
      break;
    default: {
      const _never: never = action;
      void _never;
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════
// Visibility state mutations
// ═══════════════════════════════════════════════════════════════════════

function requireVisibilityParameter(
  document: DrawingDocument,
  blockName: string,
  parameterId: string,
): {
  blockDef: BlockDefinition;
  meta: DynamicBlockMetadata;
  parameter: VisibilityParameter;
  parameterIndex: number;
} {
  const blockDef = requireBlock(document, blockName);
  const meta = ensureMetadata(blockDef);
  const idx = meta.parameters.findIndex((p) => p.id === parameterId);
  if (idx === -1) {
    throw new Error(`Parameter '${parameterId}' not found`);
  }
  const parameter = meta.parameters[idx];
  if (!parameter || parameter.kind !== "visibility") {
    throw new Error(
      `Parameter '${parameterId}' is not a visibility parameter`,
    );
  }
  return { blockDef, meta, parameter, parameterIndex: idx };
}

function replaceVisibilityParameter(
  meta: DynamicBlockMetadata,
  index: number,
  next: VisibilityParameter,
): DynamicBlockMetadata {
  const params = meta.parameters.slice();
  params[index] = next;
  return { ...meta, parameters: params };
}

export interface AddVisibilityStateInput {
  document: DrawingDocument;
  blockName: string;
  parameterId: string;
  stateId: string;
  stateName: string;
  invisibleEntityIds?: string[];
}

export function addVisibilityStateMutation(
  input: AddVisibilityStateInput,
): MutationResult {
  const { blockDef, meta, parameter, parameterIndex } =
    requireVisibilityParameter(
      input.document,
      input.blockName,
      input.parameterId,
    );
  if (parameter.states.some((s) => s.id === input.stateId)) {
    throw new Error(`State id '${input.stateId}' already exists`);
  }
  if (parameter.states.some((s) => s.name === input.stateName)) {
    throw new Error(`State name '${input.stateName}' already exists`);
  }
  const newState: VisibilityState = {
    id: input.stateId,
    name: input.stateName,
    invisibleEntityIds: input.invisibleEntityIds ?? [],
  };
  const nextParam: VisibilityParameter = {
    ...parameter,
    states: [...parameter.states, newState],
  };
  const nextMeta = replaceVisibilityParameter(meta, parameterIndex, nextParam);
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Added visibility state '${input.stateName}' to parameter '${parameter.name}'`,
  );
}

export interface RenameVisibilityStateInput {
  document: DrawingDocument;
  blockName: string;
  parameterId: string;
  stateId: string;
  newName: string;
}

export function renameVisibilityStateMutation(
  input: RenameVisibilityStateInput,
): MutationResult {
  const { blockDef, meta, parameter, parameterIndex } =
    requireVisibilityParameter(
      input.document,
      input.blockName,
      input.parameterId,
    );
  const existing = parameter.states.find((s) => s.id === input.stateId);
  if (!existing) {
    throw new Error(`State '${input.stateId}' not found`);
  }
  if (
    parameter.states.some(
      (s) => s.id !== input.stateId && s.name === input.newName,
    )
  ) {
    throw new Error(`State name '${input.newName}' already exists`);
  }
  const nextParam: VisibilityParameter = {
    ...parameter,
    states: parameter.states.map((s) =>
      s.id === input.stateId ? { ...s, name: input.newName } : s,
    ),
  };
  const nextMeta = replaceVisibilityParameter(meta, parameterIndex, nextParam);
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Renamed visibility state '${existing.name}' to '${input.newName}'`,
  );
}

export interface DeleteVisibilityStateInput {
  document: DrawingDocument;
  blockName: string;
  parameterId: string;
  stateId: string;
}

export function deleteVisibilityStateMutation(
  input: DeleteVisibilityStateInput,
): MutationResult {
  const { blockDef, meta, parameter, parameterIndex } =
    requireVisibilityParameter(
      input.document,
      input.blockName,
      input.parameterId,
    );
  if (parameter.states.length <= 1) {
    throw new Error(
      `Cannot delete last visibility state (parameter '${parameter.name}' requires at least one state)`,
    );
  }
  const removed = parameter.states.find((s) => s.id === input.stateId);
  if (!removed) {
    throw new Error(`State '${input.stateId}' not found`);
  }
  const remaining = parameter.states.filter((s) => s.id !== input.stateId);
  const firstRemaining = remaining[0];
  if (!firstRemaining) {
    // Unreachable given the `<= 1` guard above, but TypeScript narrows.
    throw new Error(`Visibility parameter '${parameter.name}' has no states`);
  }
  // Reassign defaultStateId / currentStateId if they matched the deleted.
  const nextDefault =
    parameter.defaultStateId === input.stateId
      ? firstRemaining.id
      : parameter.defaultStateId;
  const nextCurrent =
    parameter.currentStateId === input.stateId
      ? nextDefault
      : parameter.currentStateId;
  const nextParam: VisibilityParameter = {
    ...parameter,
    states: remaining,
    defaultStateId: nextDefault,
    currentStateId: nextCurrent,
  };
  const nextMeta = replaceVisibilityParameter(meta, parameterIndex, nextParam);
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Deleted visibility state '${removed.name}' from parameter '${parameter.name}'`,
  );
}

export interface ReorderVisibilityStatesInput {
  document: DrawingDocument;
  blockName: string;
  parameterId: string;
  stateIds: string[];
}

export function reorderVisibilityStatesMutation(
  input: ReorderVisibilityStatesInput,
): MutationResult {
  const { blockDef, meta, parameter, parameterIndex } =
    requireVisibilityParameter(
      input.document,
      input.blockName,
      input.parameterId,
    );
  const current = parameter.states;
  if (input.stateIds.length !== current.length) {
    throw new Error(
      `Reorder must list every state (${current.length} total, got ${input.stateIds.length})`,
    );
  }
  const presentIds = new Set(current.map((s) => s.id));
  for (const id of input.stateIds) {
    if (!presentIds.has(id)) {
      throw new Error(`Reorder references unknown state id '${id}'`);
    }
  }
  const byId = new Map(current.map((s) => [s.id, s] as const));
  const nextStates = input.stateIds.map((id) => {
    const s = byId.get(id);
    if (!s) throw new Error(`State '${id}' disappeared during reorder`);
    return s;
  });
  const nextParam: VisibilityParameter = {
    ...parameter,
    states: nextStates,
  };
  const nextMeta = replaceVisibilityParameter(meta, parameterIndex, nextParam);
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Reordered visibility states on parameter '${parameter.name}'`,
  );
}

export interface SetDefaultVisibilityStateInput {
  document: DrawingDocument;
  blockName: string;
  parameterId: string;
  stateId: string;
}

export function setDefaultVisibilityStateMutation(
  input: SetDefaultVisibilityStateInput,
): MutationResult {
  const { blockDef, meta, parameter, parameterIndex } =
    requireVisibilityParameter(
      input.document,
      input.blockName,
      input.parameterId,
    );
  const state = parameter.states.find((s) => s.id === input.stateId);
  if (!state) {
    throw new Error(`State '${input.stateId}' not found`);
  }
  const nextParam: VisibilityParameter = {
    ...parameter,
    defaultStateId: input.stateId,
  };
  const nextMeta = replaceVisibilityParameter(meta, parameterIndex, nextParam);
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Set '${state.name}' as default state for parameter '${parameter.name}'`,
  );
}

export interface ToggleVisibilityStateEntityInput {
  document: DrawingDocument;
  blockName: string;
  parameterId: string;
  stateId: string;
  entityId: string;
  visible: boolean;
}

export function toggleVisibilityStateEntityMutation(
  input: ToggleVisibilityStateEntityInput,
): MutationResult {
  const { blockDef, meta, parameter, parameterIndex } =
    requireVisibilityParameter(
      input.document,
      input.blockName,
      input.parameterId,
    );
  const state = parameter.states.find((s) => s.id === input.stateId);
  if (!state) {
    throw new Error(`State '${input.stateId}' not found`);
  }
  // visible=true → remove entityId from invisibleEntityIds
  // visible=false → add entityId to invisibleEntityIds (dedup)
  const current = new Set(state.invisibleEntityIds);
  if (input.visible) {
    current.delete(input.entityId);
  } else {
    current.add(input.entityId);
  }
  const nextStates = parameter.states.map((s) =>
    s.id === input.stateId
      ? { ...s, invisibleEntityIds: Array.from(current).sort() }
      : s,
  );
  const nextParam: VisibilityParameter = {
    ...parameter,
    states: nextStates,
  };
  const nextMeta = replaceVisibilityParameter(meta, parameterIndex, nextParam);
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `${input.visible ? "Show" : "Hide"} entity '${input.entityId}' in state '${state.name}'`,
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Phase K.4 — Set current visibility state
// ═══════════════════════════════════════════════════════════════════════
//
// AutoCAD's BVSTATE command — when invoked from a dynamic-block ribbon
// drop-down or via the Block Editor's state picker — sets the
// `currentStateId` of the visibility parameter without changing the
// `defaultStateId`. The renderer (canvas + Properties palette) reads
// `currentStateId` to know which state's `invisibleEntityIds` filter
// out which children. New entities created in BEDIT inherit the
// AutoCAD-default behaviour: visible in ALL states (per spec §3.2.1),
// implemented by the geometry reducer (NOT here — this mutation only
// flips the current-state pointer).

export interface SetCurrentVisibilityStateInput {
  document: DrawingDocument;
  blockName: string;
  parameterId: string;
  stateId: string;
}

export function setCurrentVisibilityStateMutation(
  input: SetCurrentVisibilityStateInput,
): MutationResult {
  const { blockDef, meta, parameter, parameterIndex } =
    requireVisibilityParameter(
      input.document,
      input.blockName,
      input.parameterId,
    );
  const state = parameter.states.find((s) => s.id === input.stateId);
  if (!state) {
    throw new Error(`State '${input.stateId}' not found`);
  }
  const nextParam: VisibilityParameter = {
    ...parameter,
    currentStateId: input.stateId,
  };
  const nextMeta = replaceVisibilityParameter(meta, parameterIndex, nextParam);
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Set current visibility state to '${state.name}' on parameter '${parameter.name}'`,
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Phase K.4 — BVHIDE / BVSHOW / BVSHOW _All
// ═══════════════════════════════════════════════════════════════════════
//
// AutoCAD's per-spec semantics (block-authoring-spec.md §3.2):
//
//   BVHIDE       — "Make Invisible" — hides the listed entities IN THE
//                  CURRENT VISIBILITY STATE only. Adds each entity id
//                  to that state's invisibleEntityIds (dedup). Other
//                  states are unaffected.
//   BVSHOW       — "Make Visible (current state only)" — removes each
//                  entity id from the current state's invisibleEntityIds.
//                  Other states are unaffected.
//   BVSHOW _All  — "Make Visible in all states" — removes each entity
//                  id from EVERY state's invisibleEntityIds.
//
// All three commands operate on a list (toolbar selection set may
// contain >1 entity). Single-entity callers wrap a one-element array.
// Storage uses `invisibleEntityIds` (DXF group 332 list) for parity
// with the existing parser/writer round-trip; the spec doc §3.1
// describes the same set as "VISIBLE entities" because AutoCAD's group
// 332 is interpreted by the *renderer*, but our internal serialisation
// has historically captured the list as invisible — we keep that
// invariant here to avoid touching parsers / 100+ existing fixtures.
// External AutoCAD parity is preserved because the *semantic*
// `BVHIDE`/`BVSHOW` operations always produce the same final
// rendered visibility set.

export interface BvhideInput {
  document: DrawingDocument;
  blockName: string;
  parameterId: string;
  /** State whose invisibleEntityIds list is mutated; usually `currentStateId`. */
  stateId: string;
  /** Entity ids to hide in this state. */
  entityIds: readonly string[];
}

/**
 * BVHIDE — hide entities in the current state only.
 *
 * Idempotent: re-hiding an already-hidden entity is a no-op (Set-based
 * dedup). Empty `entityIds` is a structural no-op (returns the input
 * document unchanged). Unknown entity ids are accepted (caller may
 * pass an id from a sibling block-edit space; the state stores raw
 * handles and the renderer simply will never match an unknown).
 */
export function bvhideMutation(input: BvhideInput): MutationResult {
  const { blockDef, meta, parameter, parameterIndex } =
    requireVisibilityParameter(
      input.document,
      input.blockName,
      input.parameterId,
    );
  const state = parameter.states.find((s) => s.id === input.stateId);
  if (!state) {
    throw new Error(`State '${input.stateId}' not found`);
  }
  if (input.entityIds.length === 0) {
    return { updatedDocument: input.document, summary: "no-op" };
  }
  const merged = new Set(state.invisibleEntityIds);
  for (const id of input.entityIds) merged.add(id);
  const nextStates = parameter.states.map((s) =>
    s.id === input.stateId
      ? { ...s, invisibleEntityIds: Array.from(merged).sort() }
      : s,
  );
  const nextParam: VisibilityParameter = {
    ...parameter,
    states: nextStates,
  };
  const nextMeta = replaceVisibilityParameter(meta, parameterIndex, nextParam);
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `BVHIDE: hidden ${input.entityIds.length} entity(ies) in state '${state.name}'`,
  );
}

export interface BvshowInput {
  document: DrawingDocument;
  blockName: string;
  parameterId: string;
  /** State whose invisibleEntityIds list is mutated; usually `currentStateId`. */
  stateId: string;
  /** Entity ids to make visible in this state. */
  entityIds: readonly string[];
}

/**
 * BVSHOW (current state only) — remove entities from the current
 * state's invisibleEntityIds list. Mirrors BVHIDE structure but
 * subtracts instead of adds.
 */
export function bvshowMutation(input: BvshowInput): MutationResult {
  const { blockDef, meta, parameter, parameterIndex } =
    requireVisibilityParameter(
      input.document,
      input.blockName,
      input.parameterId,
    );
  const state = parameter.states.find((s) => s.id === input.stateId);
  if (!state) {
    throw new Error(`State '${input.stateId}' not found`);
  }
  if (input.entityIds.length === 0) {
    return { updatedDocument: input.document, summary: "no-op" };
  }
  const remove = new Set(input.entityIds);
  const filtered = state.invisibleEntityIds.filter((id) => !remove.has(id));
  // No change → no-op.
  if (filtered.length === state.invisibleEntityIds.length) {
    return { updatedDocument: input.document, summary: "no-op" };
  }
  const nextStates = parameter.states.map((s) =>
    s.id === input.stateId ? { ...s, invisibleEntityIds: filtered } : s,
  );
  const nextParam: VisibilityParameter = {
    ...parameter,
    states: nextStates,
  };
  const nextMeta = replaceVisibilityParameter(meta, parameterIndex, nextParam);
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `BVSHOW: shown ${input.entityIds.length} entity(ies) in state '${state.name}'`,
  );
}

export interface BvshowAllInput {
  document: DrawingDocument;
  blockName: string;
  parameterId: string;
  /** Entity ids to make visible in ALL states. */
  entityIds: readonly string[];
}

/**
 * BVSHOW _All — remove entities from EVERY state's invisibleEntityIds.
 * Used when the user picks the "Make Visible — All states" toolbar
 * option (AutoCAD's `_All` keyword on BVSHOW).
 */
export function bvshowAllMutation(input: BvshowAllInput): MutationResult {
  const { blockDef, meta, parameter, parameterIndex } =
    requireVisibilityParameter(
      input.document,
      input.blockName,
      input.parameterId,
    );
  if (input.entityIds.length === 0) {
    return { updatedDocument: input.document, summary: "no-op" };
  }
  const remove = new Set(input.entityIds);
  let changed = false;
  const nextStates = parameter.states.map((s) => {
    const filtered = s.invisibleEntityIds.filter((id) => !remove.has(id));
    if (filtered.length !== s.invisibleEntityIds.length) {
      changed = true;
      return { ...s, invisibleEntityIds: filtered };
    }
    return s;
  });
  if (!changed) {
    return { updatedDocument: input.document, summary: "no-op" };
  }
  const nextParam: VisibilityParameter = {
    ...parameter,
    states: nextStates,
  };
  const nextMeta = replaceVisibilityParameter(meta, parameterIndex, nextParam);
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `BVSHOW _All: shown ${input.entityIds.length} entity(ies) in all states of '${parameter.name}'`,
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Chain group mutations (DYN-3 Batch C)
// ═══════════════════════════════════════════════════════════════════════
//
// `evaluationGraph.chainGroups` is a list of ordered action-id arrays.
// Each inner array is a sequential pipeline — the evaluator processes
// it by feeding the output of one action as the input of the next.
// Actions not referenced by any group run independently on the original
// geometry (matching pre-DYN-3 behaviour).
//
// These mutations are convenience helpers that keep `chainGroups` in a
// canonical, well-formed shape (no empty groups, unique membership).
// All mutations re-validate the metadata afterwards.

export interface CreateChainGroupInput {
  document: DrawingDocument;
  blockName: string;
  actionIds: string[];
}

/**
 * Create a new chain group containing the listed action ids. When
 * `actionIds` is empty the mutation is a no-op (empty groups are
 * filtered by validate anyway). All referenced actions must exist.
 * Actions already a member of another group are silently removed from
 * that group first — each action may participate in at most one chain
 * group at a time (AutoCAD parity).
 */
export function createChainGroupMutation(
  input: CreateChainGroupInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  if (input.actionIds.length === 0) {
    // No-op — return current document unchanged.
    return { updatedDocument: input.document, summary: "no-op" };
  }
  const validIds = new Set(meta.actions.map((a) => a.id));
  for (const id of input.actionIds) {
    if (!validIds.has(id)) {
      throw new Error(`Chain group references unknown action '${id}'`);
    }
  }
  // Dedupe: drop the requested ids from every existing group first.
  const pruned = meta.evaluationGraph.chainGroups
    .map((g) => g.filter((id) => !input.actionIds.includes(id)))
    .filter((g) => g.length > 0);
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    evaluationGraph: {
      ...meta.evaluationGraph,
      chainGroups: [...pruned, [...input.actionIds]],
    },
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Created chain group with ${input.actionIds.length} action(s) in block '${input.blockName}'`,
  );
}

export interface AddActionToChainGroupInput {
  document: DrawingDocument;
  blockName: string;
  actionId: string;
  /**
   * Target group index (0-based). If omitted or out of range, the
   * action is appended to a new group. If a group already contains the
   * action, membership is moved to the end of the target group.
   */
  groupIndex?: number;
}

export function addActionToChainGroupMutation(
  input: AddActionToChainGroupInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  if (!meta.actions.some((a) => a.id === input.actionId)) {
    throw new Error(`Action '${input.actionId}' not found`);
  }
  // Remove the action from any existing group.
  const withoutAction = meta.evaluationGraph.chainGroups
    .map((g) => g.filter((id) => id !== input.actionId))
    .filter((g) => g.length > 0);
  const targetIndex =
    input.groupIndex !== undefined &&
    input.groupIndex >= 0 &&
    input.groupIndex < withoutAction.length
      ? input.groupIndex
      : -1;
  let nextGroups: string[][];
  if (targetIndex === -1) {
    // New group.
    nextGroups = [...withoutAction, [input.actionId]];
  } else {
    nextGroups = withoutAction.map((g, i) =>
      i === targetIndex ? [...g, input.actionId] : g,
    );
  }
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    evaluationGraph: { ...meta.evaluationGraph, chainGroups: nextGroups },
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Added action '${input.actionId}' to chain ${
      targetIndex === -1 ? "(new group)" : `group ${targetIndex + 1}`
    } in block '${input.blockName}'`,
  );
}

export interface RemoveActionFromChainInput {
  document: DrawingDocument;
  blockName: string;
  actionId: string;
}

/**
 * Remove the action from whichever chain group it belongs to. Drops
 * empty groups as a side-effect. If the action is not in any group,
 * this is a silent no-op (still returns the current document).
 */
export function removeActionFromChainMutation(
  input: RemoveActionFromChainInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  const nextGroups = meta.evaluationGraph.chainGroups
    .map((g) => g.filter((id) => id !== input.actionId))
    .filter((g) => g.length > 0);
  if (
    JSON.stringify(nextGroups) ===
    JSON.stringify(meta.evaluationGraph.chainGroups)
  ) {
    return { updatedDocument: input.document, summary: "no-op" };
  }
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    evaluationGraph: { ...meta.evaluationGraph, chainGroups: nextGroups },
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Removed action '${input.actionId}' from chain in block '${input.blockName}'`,
  );
}

export interface ReorderChainGroupInput {
  document: DrawingDocument;
  blockName: string;
  groupIndex: number;
  actionIds: string[];
}

/**
 * Replace the action-id order of the chain group at `groupIndex`.
 * `actionIds` must list exactly the ids currently in that group (no
 * insertions / deletions — use add/remove mutations for that).
 */
export function reorderChainGroupMutation(
  input: ReorderChainGroupInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  const group = meta.evaluationGraph.chainGroups[input.groupIndex];
  if (!group) {
    throw new Error(`Chain group ${input.groupIndex} not found`);
  }
  const currentSet = new Set(group);
  const targetSet = new Set(input.actionIds);
  if (
    currentSet.size !== targetSet.size ||
    ![...currentSet].every((id) => targetSet.has(id))
  ) {
    throw new Error(
      `Reorder must list exactly the ids in chain group ${input.groupIndex}`,
    );
  }
  const nextGroups = meta.evaluationGraph.chainGroups.map((g, i) =>
    i === input.groupIndex ? [...input.actionIds] : g,
  );
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    evaluationGraph: { ...meta.evaluationGraph, chainGroups: nextGroups },
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Reordered chain group ${input.groupIndex + 1} in block '${input.blockName}'`,
  );
}
